import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { subscriptionLicenseApi, useCancelPendingSubscriptionMutation } from "../../../services/subscription/subscriptionLicense.rtk.api";
import { RootState } from "../../../redux/store";
import Assets from "../../../core/assets";
import AddScreenModal from "./screenModal/AddLicensesModal";
import ConfirmActionModal from "./screenModal/ConfirmActionModal";
import UpgradeModal from "./screenModal/UpgradeModal";
import ComparePlansModal from "./screenModal/ComparePlansModal";
import AddonScreensModal from "./screenModal/AddonScreensModal";
import LicenseDetailsModal from "./screenModal/LicenseDetailsModal";
import {
  useDowngradeLicenseMutation,
  useUpgradeLicenseMutation,
  useCancelLicenseMutation,
  useGetLicensesQuery,
  useGetOrganisationScreensQuery,
  useCancelAddonScreenMutation,
  useGetPlansQuery,
  useResubscribeLicenseMutation,
  useRevokeLicenseMutation,
  useGetOrganisationByIdQuery,
  useGetSubscriptionsSummaryQuery,
} from "../../../services";
import { ScreenRow } from "../../../types";
import {
  LicensesType,
  PlanVariant,
  PlanBillingCycle,
} from "../../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import AddonScreenDetailsModal from "./screenModal/AddonScreenDetailsModal";
import DeleteLicenseModal from "./screenModal/DeleteLicenseModal";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import DowngradeModal from "./screenModal/DowngradeModal";
import { toast } from "../../../core/toast";
import { loadRazorpayScript } from "../../../utils/loadRazorpay";
import CancelPaymentConfirmation from "./screenModal/CancelPaymentConfirmation";

const PAGE_SIZE = 20;

function CountdownTimer({ createdAt }: { createdAt: string }) {
  const [timeLeft, setTimeLeft] = useState('00:00:00');

  useEffect(() => {
    if (!createdAt) return;

    // ✅ 15 minutes in milliseconds
    const expiryTime = new Date(createdAt).getTime() + (15 * 60 * 1000);

    const updateTimer = () => {
      const now = new Date().getTime();
      const distance = expiryTime - now;

      if (distance <= 0) {
        setTimeLeft('00:00:00');
        return;
      }

      const minutes = Math.floor((distance / (1000 * 60)));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft(
        `00:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);

    return () => clearInterval(intervalId);
  }, [createdAt]);

  return <>{timeLeft}</>;
}
const Licenses: React.FC = () => {
  const { theme } = useTheme();
  const themeBox = theme === "dark";

  // Get selected organization ID from Redux
  const organisationId = useAppSelector(selectOrganisationId);
  const dispatch = useAppDispatch();
  const [showAddScreenModal, setShowAddScreenModal] = useState(false);
  const [showAddonScreensModal, setShowAddonScreensModal] = useState(false);
  const [selectedLicenseForAddon, setSelectedLicenseForAddon] = useState<
    any | null
  >(null);
  const [showAddonDetailsModal, setShowAddonDetailsModal] = useState(false);
  const [addonDetailsScreens, setAddonDetailsScreens] = useState<any[]>([]);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<ScreenRow | null>(null);
  const [showComparePlansModal, setShowComparePlansModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ScreenRow | null>(null);
  const [showLicenseDetailsModal, setShowLicenseDetailsModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<any>(null);
  const [licenseDetailsScreens, setLicenseDetailsScreens] = useState<any>({
    licenseScreens: [],
    addOnScreens: [],
  });
  const [showDeleteLicenseModal, setShowDeleteLicenseModal] = useState(false);
  const [deleteModalScreens, setDeleteModalScreens] = useState<any>({
    licenseScreens: [],
    addOnScreens: [],
  });
  const [showAddonDeleteConfirm, setShowAddonDeleteConfirm] = useState(false);
  const [addonDeleteInfo, setAddonDeleteInfo] = useState<{
    licenseId: string;
    screenId: string;
  } | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [showResubscribeConfirm, setShowResubscribeConfirm] = useState(false);
  const [resubscribeTarget, setResubscribeTarget] = useState<string | null>(
    null,
  );
  const [isPendingPurchaseOpen, setIsPendingPurchaseOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [licensesList, setLicensesList] = useState<any[]>([]);
  const [timeLeft, setTimeLeft] = useState<string>('00:00:00');
  const [showPendingPopup, setShowPendingPopup] = useState<boolean>(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showCancelPaymentConfirmation, setShowCancelPaymentConfirmation] = useState<boolean>(false)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isRazorpayOpen, setIsRazorpayOpen] = useState(false);
  const {
    data: licensesData,
    isLoading: licensesLoading,
    isFetching: licensesFetching,
  } = useGetLicensesQuery({ organisationId, page, limit: 20 });

  React.useEffect(() => {
    setPage(1);
    setLicensesList([]);
  }, [organisationId]);

  React.useEffect(() => {
    if (licensesData?.results) {
      if (page === 1) {
        setLicensesList(licensesData.results);
      } else {
        setLicensesList((prev) => {
          const prevIds = new Set(prev.map((p) => p._id));
          const newItems = licensesData.results.filter(
            (item: any) => !prevIds.has(item._id)
          );
          return [...prev, ...newItems];
        });
      }
    }
  }, [licensesData, page]);
  const { data: screensData } = useGetOrganisationScreensQuery({
    organisationId,
  });

  const { data: orgData } = useGetOrganisationByIdQuery({
    orgid: organisationId,
  });
  const { data: summaryData } = useGetSubscriptionsSummaryQuery(
    { organisationId: organisationId || "" },
    { skip: !organisationId }
  );
  const { data: plansData } = useGetPlansQuery();

  const plans: any[] = useMemo(() => {
    if (!plansData) return [];
    return Array.isArray((plansData as any)?.results)
      ? (plansData as any).results
      : Array.isArray((plansData as any)?.data?.results)
        ? (plansData as any).data.results
        : Array.isArray(plansData)
          ? (plansData as any[])
          : [];
  }, [plansData]);

  // Extract all screens from newly created RTK response
  const allScreens = useMemo(() => {
    if (!screensData?.results) return [];
    return Array.isArray(screensData.results) ? screensData.results : [];
  }, [screensData]);

  const [cancelLicense] = useCancelLicenseMutation();
  const [cancelAddon] = useCancelAddonScreenMutation();
  const [upgradeLicense] = useUpgradeLicenseMutation();
  const [downgradeLicense] = useDowngradeLicenseMutation();
  const [resubscribeLicense] = useResubscribeLicenseMutation();
  const [revokeLicense] = useRevokeLicenseMutation();

  const paymentProvider = (orgData as any)?.gateway?.provider?.toLowerCase() || (orgData as any)?.result?.gateway?.provider?.toLowerCase() || "stripe";
  const isRazorpay = paymentProvider === "razorpay";
  const hideUpgradeDowngrade = isRazorpay && import.meta.env.VITE_HIDE_UPGRADE_DOWNGRADE_IN_RAZORPAY === "YES";

  const nextBillDate =
    orgData?.billing?.nextBillingOn ||
    (orgData as any)?.result?.billing?.nextBillingOn;

  const formattedBillDate = nextBillDate
    ? new Date(nextBillDate).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
    : "--/--/----";

  const overallTotalAmount =
    orgData?.billing?.totalAmount ||
    (orgData as any)?.result?.billing?.totalAmount ||
    0;
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    const totalPages = licensesData?.totalPages || 1;
    if (nearBottom && !licensesFetching && page < totalPages) {
      setPage((prev) => prev + 1);
    }
  };

  const openAddScreenModal = () => setShowAddScreenModal(true);
  const closeAddScreenModal = () => setShowAddScreenModal(false);

  const openAddonScreensModal = (license: any) => {
    setSelectedLicenseForAddon(license);
    setShowAddonScreensModal(true);
  };
  const closeAddonScreensModal = () => {
    setSelectedLicenseForAddon(null);
    setShowAddonScreensModal(false);
  };
  const openAddonDetailsModal = (license: any) => {
    setSelectedLicense(license);
    if (allScreens.length > 0) {
      const relevant = allScreens.filter((s: any) => s.license === license._id);
      const isAddon = (s: any) => {
        const v = s?.variant || s?.plan?.variant || s?.planVariant || s?.type;
        return String(v || "").toUpperCase() === "ADDON_SCREEN";
      };
      const addOnScreens = relevant.filter(isAddon);
      setAddonDetailsScreens(addOnScreens);
    } else {
      setAddonDetailsScreens([]);
    }
    setShowAddonDetailsModal(true);
  };
  const closeAddonDetailsModal = () => {
    setSelectedLicense(null);
    setAddonDetailsScreens([]);
    setShowAddonDetailsModal(false);
  };

  const openDeleteLicenseModal = (license: any) => {
    setSelectedLicense(license);
    const relevant = allScreens.filter((s: any) => s.license === license._id);
    const isAddon = (s: any) => {
      const v = s?.variant || s?.plan?.variant || s?.planVariant || s?.type;
      return String(v || "").toUpperCase() === "ADDON_SCREEN";
    };
    const addOnScreens = relevant.filter(isAddon);
    const licenseScreens = relevant.filter((s: any) => !isAddon(s));
    setDeleteModalScreens({ licenseScreens, addOnScreens });
    setShowDeleteLicenseModal(true);
  };
  const closeDeleteLicenseModal = () => {
    setSelectedLicense(null);
    setDeleteModalScreens({ licenseScreens: [], addOnScreens: [] });
    setShowDeleteLicenseModal(false);
  };

  const openLicenseDetailsModal = (license: any) => {
    setSelectedLicense(license);
    if (allScreens.length > 0) {
      const relevant = allScreens.filter((s: any) => s.license === license._id);
      const isAddon = (s: any) => {
        const v = s?.variant || s?.plan?.variant || s?.planVariant || s?.type;
        return String(v || "").toUpperCase() === "ADDON_SCREEN";
      };
      const addOnScreens = relevant.filter(isAddon);
      const licenseScreens = relevant.filter((s: any) => !isAddon(s));
      setLicenseDetailsScreens({ licenseScreens, addOnScreens });
    } else {
      setLicenseDetailsScreens({ licenseScreens: [], addOnScreens: [] });
    }
    setShowLicenseDetailsModal(true);
  };
  const closeLicenseDetailsModal = () => {
    setSelectedLicense(null);
    setLicenseDetailsScreens({ licenseScreens: [], addOnScreens: [] });
    setShowLicenseDetailsModal(false);
  };
  const openRevokeConfirm = (licenseId: string) => {
    setRevokeTarget(licenseId);
    setShowRevokeConfirm(true);
  };

  const closeRevokeConfirm = () => {
    setRevokeTarget(null);
    setShowRevokeConfirm(false);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeLicense({ licenseId: revokeTarget }).unwrap();
    } catch (e) {
      console.error("Failed to revoke license:", e);
    }
    closeRevokeConfirm();
  };

  const openResubscribeConfirm = (licenseId: string) => {
    setResubscribeTarget(licenseId);
    setShowResubscribeConfirm(true);
  };

  const closeResubscribeConfirm = () => {
    setResubscribeTarget(null);
    setShowResubscribeConfirm(false);
  };

  const confirmResubscribe = async () => {
    if (!resubscribeTarget) return;
    try {
      await resubscribeLicense({ licenseId: resubscribeTarget }).unwrap();
    } catch (e) {
      console.error("Failed to resubscribe license:", e);
    }
    closeResubscribeConfirm();
  };

  const handleScreenAdded = () => {
    closeAddScreenModal();
    closeAddonScreensModal();

  };
  const handlePaymentFailed = () => {

    setShowPendingPopup(true)
    closeAddScreenModal();
    closeAddonScreensModal();

  }
  const handleSuccess = async () => {
    setIsPaymentProcessing(true); // 🔥 start loader

    try {
      // wait for backend / RTK refetch
      await new Promise((res) => setTimeout(res, 1000)); // small delay (UX smoothness)

      // 🔥 close everything AFTER processing
      setShowPendingPopup(false);
      closeAddScreenModal();
      closeAddonScreensModal();

      // optional: show toast instead of popup
      // toast.success("Payment successful");
    } catch (err) {
      console.error(err);
    } finally {
      setIsPaymentProcessing(false);
    }
  };
  const openDowngradeModal = (license: any) => {
    const selected = {
      license: license._id,
      licenseId: license._id,
      currentPlan: license.plan,
      targetPlanId: license.targetPlanId,
      planId: license.targetPlanId,
      endDate: license.endDate,
      billDate: license.startDate,
      action: "Downgrade",
    } as any;
    setSelectedScreen(selected);
    setShowDowngradeModal(true);
  };

  const closeDowngradeModal = () => {
    setSelectedScreen(null);
    setShowDowngradeModal(false);
  };

  const openUpgradeModal = (license: any) => {
    const selected = {
      license: license._id,
      licenseId: license._id,
      currentPlan: license.plan,
      targetPlanId: license.targetPlanId,
      planId: license.targetPlanId,
      endDate: license.endDate,
      billDate: license.startDate,
      action: "Upgrade",
    } as any;
    setSelectedScreen(selected);
    setShowUpgradeModal(true);
  };

  const closeUpgradeModal = () => {
    setSelectedScreen(null);
    setShowUpgradeModal(false);
  };

  const openComparePlansModal = () => setShowComparePlansModal(true);
  const closeComparePlansModal = () => setShowComparePlansModal(false);

  const openDelete = (row: ScreenRow) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const licenseId = deleteTarget.screenId;
    if (!licenseId) return;
    try {
      await cancelLicense({ licenseId }).unwrap();
      console.log("License cancelled successfully!");
    } catch (e) {
      console.error("Failed to cancel license:", e);
    }
    closeDelete();
  };
  const handleOpenAddonDelete = (licenseId: string, screenId: string) => {
    setAddonDeleteInfo({ licenseId, screenId });
    setShowAddonDeleteConfirm(true);
  };

  const closeAddonDelete = () => {
    setAddonDeleteInfo(null);
    setShowAddonDeleteConfirm(false);
  };

  const handleConfirmAddonDelete = async () => {
    if (!addonDeleteInfo) return;
    try {
      await cancelAddon({ addonScreenId: addonDeleteInfo.screenId }).unwrap();
      console.log("Addon screen deleted successfully!");
    } catch (e) {
      console.error("Failed to delete addon screen:", e);
    }
    closeAddonDelete();
  };

  const confirmDowngrade = async () => {
    if (!selectedScreen) return;
    const licenseId =
      (selectedScreen as any).licenseId ||
      (selectedScreen as any).license ||
      selectedScreen.screenId;

    if (!licenseId) {
      console.error("Missing licenseId for downgrade", {
        licenseId,
        selectedScreen,
      });
      return;
    }
    try {
      await downgradeLicense({
        licenseId,
        planId: selectedScreen.planId,
      }).unwrap();
      console.log("Screen downgraded successfully!");
    } catch (e) {
      console.error("Failed to downgrade screen:", e);
    }
    closeDowngradeModal();
    closeUpgradeModal();
  };

  const confirmUpgrade = async (details?: {
    screenId?: string;
    licenseId?: string;
    targetPlanId?: string;
  }) => {
    if (!selectedScreen && !details) return;
    const screenId = details?.screenId || (selectedScreen as any)?.screenId;
    const planId =
      details?.targetPlanId ||
      (selectedScreen as any).targetPlanId ||
      (selectedScreen as any).planId;
    const licenseId =
      details?.licenseId ||
      (selectedScreen as any)?.licenseId ||
      (selectedScreen as any)?._id ||
      (selectedScreen as any)?.license;
    if (!licenseId || !planId) return;
    try {
      const resp = await upgradeLicense({ licenseId, planId }).unwrap();
      if (resp?.result?.invoice?.invoiceUrl) {
        window.open(resp.result.invoice.invoiceUrl, "_blank");
      } else {
      }
      console.log("Screen upgraded successfully!");
    } catch (e) {
      console.error("Failed to upgrade screen:", e);
    }
    closeDowngradeModal();
    closeUpgradeModal();
  };
  const pendingItem = summaryData?.results?.find((s: any) => String(s.status).toLowerCase() === "pending");
  const isExpired = pendingItem
    ? Date.now() > new Date(pendingItem.createdAt).getTime() + (15 * 60 * 1000)
    : true;
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!pendingItem) return;

    const interval = setInterval(() => {
      forceUpdate(prev => prev + 1);
    }, 1000); // update every second

    return () => clearInterval(interval);
  }, [pendingItem]);

  const handleRepayment = async () => {
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) return;

      const subId = pendingItem?.gateway?.razorpay?.subscriptionId;
      if (!subId) return;

      // 🔥 hide popup immediately
      setIsRazorpayOpen(true);

      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        subscription_id: subId,
        name: "GYMSTREAM PRIVATE LIMITED",
        image: "https://assets.gymstream.app/assets/site/fav-gymstream.png",
        description: "Retry Payment",

        handler: async function (response: any) {
          console.log("Repayment success", response);

          // 🔥 close Razorpay state
          setIsRazorpayOpen(false);

          // 🔥 show processing loader
          setIsPaymentProcessing(true);

          try {
            // trigger refetch
            dispatch(
              subscriptionLicenseApi.util.invalidateTags([
                "SubscriptionLicenses",
                "SubscriptionScreens",
                "LicensesAddon",
              ])
            );

            // small delay for UX
            await new Promise((res) => setTimeout(res, 1000));

            // 🔥 close popup AFTER processing
            setIsPendingPurchaseOpen(false);
          } finally {
            setIsPaymentProcessing(false);
          }
        },

        modal: {
          ondismiss: function () {
            console.log("Razorpay closed");

            setIsRazorpayOpen(false); // 👈 show popup again
          },
        },

        theme: {
          color: "#2E2E2E",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      console.error(err);
      setIsRazorpayOpen(false);
    }
  };

  const [cancelPendingSubscription, { isLoading: isCancelingPendingSubscription }] = useCancelPendingSubscriptionMutation();
  const handleCancelPendingSubscription = async () => {
    try {
      // 2. 👉 Use the trigger function here, NOT the hook itself
      await cancelPendingSubscription({ subscriptionId: pendingItem?._id }).unwrap();


      setShowCancelPaymentConfirmation(false);
    } catch (e) {
      console.error("Failed to cancel pending subscription:", e);
    }
  };



  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";


  const cardClass =
    theme === "dark"
      ? "bg-[#0A0A0A] border-none text-gray-100"
      : "bg-white  border-none text-gray-900";


  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  const scrollColors = useMemo(
    () =>
      theme === "dark"
        ? { track: "#111111", thumb: "#2A2A2A" }
        : { track: "#F5F7FA", thumb: "#C9CED6" },
    [theme],
  );




  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <style>{`
        .scroll-area {
          scrollbar-width: thin;
          scrollbar-color: var(--scroll-thumb) var(--scroll-track);
        }
        .scroll-area::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .scroll-area::-webkit-scrollbar-track {
          background: var(--scroll-track);
          border-radius: 8px;
        }
        .scroll-area::-webkit-scrollbar-thumb {
          background-color: var(--scroll-thumb);
          border-radius: 8px;
          border: 2px solid var(--scroll-track);
        }
      `}</style>

      {/* breadcrumb header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1
            className={`text-[25px] font-bold ${theme === "dark" ? "text-white" : "text-gray-900"
              } mb-2 leading-tight`}
          >
            Subscription Overview
          </h1>
          <div className="text-[14px] flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke={theme === "dark" ? "#ADADAD" : "#666666"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <Link
              to="/"
              className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
                } hover:text-[#C6D92C] transition-colors`}
            >
              Dashboard
            </Link>
            <span
              className={`${theme === "dark" ? "text-gray-600" : "text-gray-400"
                } font-bold`}
            >
              &gt;
            </span>
            <span className="text-[#C6D92C] font-normal">Licenses</span>
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-[#C6D92C] border border-[#C6D92C33] hover:bg-[#C6D92C11] transition-all"
          onClick={openComparePlansModal}
          style={{
            background:
              "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.20) 0%, rgba(83, 91, 16, 0.20) 100%)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11C3 8.17157 3 6.75736 3.87868 5.87868C4.75736 5 6.17157 5 9 5H11C13.8284 5 15.2426 5 16.1213 5.87868C17 6.75736 17 8.17157 17 11V16C17 18.8284 17 20.2426 16.1213 21.1213C15.2426 22 13.8284 22 11 22H9C6.17157 22 4.75736 22 3.87868 21.1213C3 20.2426 3 18.8284 3 16V11Z"
              stroke="white"
              stroke-width="1.5"
            />
            <path
              d="M8.04435 11.762L8.42742 11.3048C9.15621 10.4349 9.52061 10 10 10C10.4794 10 10.8438 10.4349 11.5726 11.3048L11.9556 11.762C12.6519 12.593 13 13.0086 13 13.5C13 13.9914 12.6519 14.407 11.9556 15.238L11.5726 15.6952C10.8438 16.5651 10.4794 17 10 17C9.52061 17 9.15621 16.5651 8.42742 15.6952L8.04435 15.238C7.34812 14.407 7 13.9914 7 13.5C7 13.0086 7.34812 12.593 8.04435 11.762Z"
              stroke="white"
              stroke-width="1.5"
            />
            <path
              d="M16.9244 19C18.0202 18.3874 18.3929 17.0406 19.1383 14.3469L20.1925 10.5375C20.938 7.84378 21.3107 6.49694 20.678 5.4359C20.0453 4.37485 18.6543 4.01397 15.8724 3.2922L13.9052 2.78183C11.1232 2.06006 9.73225 1.69918 8.63642 2.31177C7.85623 2.74792 7.44258 3.55626 7 4.95786"
              stroke="white"
              stroke-width="1.5"
            />
          </svg>
          Compare License
        </button>
      </div>

      {/* Header bar */}
      <div
        className={`${theme === "dark"
          ? "bg-[#0A0A0A] border-[#2A2A2A]"
          : "bg-gray-50 border-gray-200"
          } border rounded-xl mb-2 py-4 px-6 flex items-center justify-between`}
      >
        <span
          className={`text-[18px] font-bold ${theme === "dark" ? "text-white" : "text-gray-900"
            }`}
        >
          Licenses Details
        </span>
        <div className="flex items-center gap-8 text-[15px]">
          <span
            className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
              } font-medium`}
          >
            Next Bill Date:{" "}
            <span
              className={`${theme === "dark" ? "text-white" : "text-gray-900"
                } font-bold`}
            >
              {formattedBillDate}
            </span>
          </span>
          <span
            className={`${theme === "dark" ? "text-gray-400" : "text-gray-600"
              } font-medium`}
          >
            Current Subscription Amount:{" "}
            <span
              className={`${theme === "dark" ? "text-white" : "text-gray-900"
                } font-bold`}
            >
              {overallTotalAmount.toLocaleString() || "--"}
            </span>
          </span>
          <button
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-[#C6D92C] border border-[#C6D92C33] hover:bg-[#C6D92C11] transition-all"
            onClick={openAddScreenModal}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12.002 14H12.502"
                stroke="#C6D92C"
                strokeWidth="2"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
              <path d="M6.00195 22H18.002" stroke="#C6D92C" strokeWidth="1.5" />
              <path
                d="M16.0057 8.99017L12.1723 2.25855C12.0693 2.02518 12.0273 1.95688 11.9886 2.02519L8.03382 8.96903L3.11923 7.0135C3.04469 6.98332 2.9396 6.91808 3.04365 7.11616C3.04751 7.12351 3.05065 7.13149 3.05267 7.13954L6.01756 19L18.0293 18.9578L20.9909 7.05663C21.0111 6.97697 21.0013 6.97596 20.9253 7.00752L16.0057 8.99017Z"
                stroke={theme === "dark" ? "#F3F3F6" : "#666666"}
                strokeWidth="1.5"
              />
            </svg>
            Add New License
          </button>
        </div>
      </div>

      {/* Licenses list card – separate container */}
      <div className={`${cardClass}  `}>
        <div
          className="max-h-[60vh] overflow-y-auto custom-scrollbar scroll-area  bg-transparent"
          onScroll={handleScroll}
          style={
            {
              ["--scroll-track" as any]: scrollColors.track,
              ["--scroll-thumb" as any]: scrollColors.thumb,
            } as React.CSSProperties
          }
        >
          <div className="px-2 py-2 space-y-4 bg-transparent">
            <div>
              {licensesList.length === 0 && !licensesLoading ? (
                <div className="text-sm text-gray-500">
                  No licenses available. Click "Add New License" to create one.
                </div>
              ) : (
                <div className="grid gap-5 xl:grid-cols-2">
                  {licensesList.map((license: any) => (
                    <div
                      key={license._id}
                      className={`p-5 ${theme === "dark" ? "bg-[#161616]" : "bg-white"
                        } rounded-2xl border flex flex-col justify-between ${theme === "dark"
                          ? "border-white/5 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.5)]"
                          : "border-gray-200"
                        }`}
                    >
                      <div className={`flex items-center justify-between gap-3 w-full pb-4 border-b ${theme === "dark" ? "border-white/5" : "border-gray-100"}`}>
                        <div
                          className={`text-[13px] font-medium pt-1 ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                            }`}
                        >
                          {license.assignedGym?.name ? (
                            <>
                              License assigned to:{" "}
                              <span
                                className={`text-[14px] font-bold tracking-wide ${theme === "dark"
                                  ? "text-white"
                                  : "text-gray-900"
                                  }`}
                              >
                                {license.assignedGym.name}
                              </span>
                            </>
                          ) : (
                            <div className="text-[11px] text-[#C6D92C] font-medium mt-1 flex items-center gap-1">
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C6D92C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                              </svg>
                              License not yet assigned to a gym
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Status badge */}
                          {(() => {
                            const status = license.status || "--";
                            const normalizedStatus = status.toLowerCase();

                            const labelColor =
                              normalizedStatus === "active"
                                ? "text-[#C6D92C]"
                                : normalizedStatus === "pending"
                                  ? "text-yellow-400"
                                  : normalizedStatus === "expired"
                                    ? "text-orange-400"
                                    : normalizedStatus === "cancelled"
                                      ? "text-red-400"
                                      : "text-[#949494]";

                            return (
                              <span className="flex items-center gap-1 text-[13px] font-semibold text-[#949494]">
                                Status:
                                <span className={`capitalize ${labelColor}`}>{status}</span>
                              </span>
                            );
                          })()}

                          {(() => {
                            const planId = license.plan?._id || license.plan;
                            const currentPlan = plans.find(
                              (p: any) => p._id === planId,
                            );
                            const isLicenseVariant =
                              (currentPlan?.variant ||
                                license.plan?.variant ||
                                license.variant) === PlanVariant.LICENSE ||
                              (currentPlan?.name || license.plan?.name || "")
                                .toUpperCase()
                                .includes("LICENSE");
                            const isMonthly =
                              (currentPlan?.cycle ||
                                license.plan?.cycle ||
                                license.cycle) === PlanBillingCycle.MONTHLY ||
                              (currentPlan?.name || license.plan?.name || "")
                                .toUpperCase()
                                .includes("MONTHLY");

                            if (!isLicenseVariant || !isMonthly) return null;

                            if (license.type === LicensesType.BASIC) {
                              const targetPlan = plans.find(
                                (p: any) =>
                                  p.type === LicensesType.PREMIUM &&
                                  p.variant === PlanVariant.LICENSE &&
                                  p.cycle === PlanBillingCycle.MONTHLY,
                              );
                              if (!targetPlan) return null;

                              const isPremiumDisabled = import.meta.env.VITE_DISABLE_PREMIUM_PLANS === "YES";
                              if (hideUpgradeDowngrade) return null;

                              const isUpgradeDisabled = isPremiumDisabled ||
                                license.isCancelRequested ||
                                license.isCancelAccepted ||
                                license.isUpgradeRequested ||
                                license.isUpgradeAccepted ||
                                license.status?.toLowerCase() === "expired";

                              return (
                                <div className="relative group inline-block">
                                  <button
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg  text-sm font-semibold text-white border-[1.5px] border-[#C6D92C] bg-[linear-gradient(180deg,#5D6610_0%,#3B410A_100%)] shadow-[0_0_0_1px_rgba(0,0,0,0.65)] ${isUpgradeDisabled
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                      }`}
                                    onClick={() => {
                                      if (!isUpgradeDisabled) {
                                        openUpgradeModal({
                                          ...license,
                                          targetPlanId: targetPlan._id,
                                        });
                                      }
                                    }}
                                  >
                                    <div className="w-5 h-5 flex items-center justify-center ">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                      >
                                        <path
                                          d="M2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28249 21.5 7.52166 21.5 12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.5217 21.5 5.2825 20.1088 3.8912C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.8912C2.5 5.2825 2.5 7.5217 2.5 12Z"
                                          stroke="white"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M16 11.5C16 11.5 13.0541 7.5 12 7.5C10.9459 7.5 8 11.5 8 11.5M12 8V16.5"
                                          stroke="#C6D92C"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </div>
                                    {license.isUpgradeAccepted
                                      ? "Upgraded"
                                      : license.isUpgradeRequested
                                        ? "Upgrading"
                                        : "Upgrade"}
                                  </button>

                                  {/* Custom Tooltip */}
                                  {isUpgradeDisabled && (
                                    <div
                                      className={`pointer-events-none absolute top-[115%] left-1/2 -translate-x-1/2 mt-2 w-max max-w-[200px] text-center px-3 py-2 rounded-lg text-[13px] font-medium leading-tight shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 ${theme === "dark"
                                        ? "bg-[#282828] text-white border border-white/10"
                                        : "bg-gray-800 text-white border border-gray-700"}`}
                                    >
                                      {isPremiumDisabled
                                        ? "Currently Unavailable"
                                        : license.status?.toLowerCase() === "expired"
                                          ? "The license is expired, so an upgrade action cannot be performed."
                                          : license.isCancelRequested || license.isCancelAccepted
                                            ? "Canceled license can't be upgraded."
                                            : license.isUpgradeRequested || license.isUpgradeAccepted
                                              ? `License is already ${license.isUpgradeAccepted ? "upgraded" : "upgrading"}.`
                                              : ""}
                                      {/* Arrow */}
                                      <div className={`absolute bottom-full left-1/2 -translate-x-1/2 border-[5px] border-transparent ${theme === "dark" ? "border-b-[#282828]" : "border-b-gray-800"}`}></div>
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            if (license.type === LicensesType.PREMIUM) {
                              const targetPlan = plans.find(
                                (p: any) =>
                                  p.type === LicensesType.BASIC &&
                                  p.variant === PlanVariant.LICENSE &&
                                  p.cycle === PlanBillingCycle.MONTHLY,
                              );
                              if (!targetPlan) return null;
                              if (hideUpgradeDowngrade) return null;

                              return (
                                <button
                                  className={`px-3 py-1.5 rounded-lg text-[14px] font-semibold flex items-center gap-2 border ${theme === "dark"
                                    ? "text-white border-[#3A3A3A] bg-[#00000000]"
                                    : "text-gray-600 border-gray-200 bg-gray-50"
                                    } 
                                    ${license.isCancelRequested ||
                                      license.isCancelAccepted ||
                                      license.isDowngradeRequested ||
                                      license.isDowngradeAccepted ||
                                      license.status?.toLowerCase() === "expired"
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                    }`}
                                  onClick={() => {
                                    if (
                                      !(
                                        license.isCancelRequested ||
                                        license.isCancelAccepted ||
                                        license.isDowngradeRequested ||
                                        license.isDowngradeAccepted ||
                                        license.status?.toLowerCase() === "expired"
                                      )
                                    ) {
                                      openDowngradeModal({
                                        ...license,
                                        targetPlanId: targetPlan._id,
                                      });
                                    }
                                  }}
                                  title={
                                    license.status?.toLowerCase() === "expired"
                                      ? "The license is expired so that downgrade action not able done"
                                      : license.isCancelRequested ||
                                        license.isCancelAccepted
                                        ? "Canceled license can't be downgraded"
                                        : license.isDowngradeRequested ||
                                          license.isDowngradeAccepted
                                          ? `License is already ${license.isDowngradeAccepted
                                            ? "downgraded"
                                            : "downgrading"
                                          }`
                                          : ""
                                  }
                                >
                                  <div
                                    className={`w-5 h-5 flex items-center justify-center border rounded ${theme === "dark"
                                      ? "border-white/20 bg-white/5"
                                      : "border-gray-300 bg-gray-100"
                                      }`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path
                                        d="M12 6V18M12 18L6 12M12 18L18 12"
                                        stroke="#C6D92C"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </div>
                                  {license.isDowngradeAccepted
                                    ? "Downgraded"
                                    : license.isDowngradeRequested
                                      ? "Downgrading"
                                      : "Downgrade"}
                                </button>
                              );
                            }

                            return null;
                          })()}

                          <button
                            className={`px-3 py-1.5 rounded-lg border text-[14px] font-bold flex items-center gap-1 transition-colors ${theme === "dark"
                              ? "border-[#3A3A3A] bg-[#0A0A0A] text-white hover:bg-white/5"
                              : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                              }`}
                            onClick={() => openLicenseDetailsModal(license)}
                          >
                            View
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M9 18L15 12L9 6"
                                stroke="#C6D92C"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {/* Show Resubscribe if cancelled, Revoke if cancel requested/accepted, otherwise proper delete/cancel */}
                          {(() => {
                            // 1. Resubscribe if fully cancelled
                            if (license.status === "cancelled") {
                              return (
                                <button
                                  className={`px-3 py-1.5 rounded-lg border text-[14px] font-bold flex items-center gap-1 transition-colors ${theme === "dark"
                                    ? "border-[#3A3A3A] bg-[#0A0A0A] text-white hover:bg-white/5"
                                    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                                    }`}
                                  onClick={() =>
                                    openResubscribeConfirm(license._id)
                                  }
                                >
                                  Resubscribe
                                </button>
                              );
                            }

                            // 2. Revoke if cancel requested or accepted (but not yet fully cancelled state if that's possible)
                            if (
                              license.isCancelRequested ||
                              license.isCancelAccepted
                            ) {
                              return (
                                <button
                                  className={`px-3 py-1.5 rounded-lg border text-[14px] font-bold flex items-center gap-1 transition-colors ${theme === "dark"
                                    ? "border-[#3A3A3A] bg-[#0A0A0A] text-white hover:bg-white/5"
                                    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                                    }`}
                                  onClick={() => openRevokeConfirm(license._id)}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#C6D92C"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="mr-1"
                                  >
                                    <path d="M1 4v6h6" />
                                    <path d="M3.51 9A9 9 0 1 0 6 6.3L1 10" />
                                  </svg>
                                  Revoke
                                </button>
                              );
                            }

                            // 3. Default: Cancel (Delete) button
                            return (
                              <button
                                className={`p-1.5 transition-colors rounded-lg ${license.isDowngradeRequested || license.status?.toLowerCase() === "expired"
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-500 hover:bg-red-500/10"
                                  }`}
                                disabled={!!license.isDowngradeRequested || license.status?.toLowerCase() === "expired"}
                                onClick={() => openDeleteLicenseModal(license)}
                                title={
                                  license.status?.toLowerCase() === "expired"
                                    ? "The license is expired so that delete action not able done"
                                    : license.isDowngradeRequested
                                      ? "already downgraded screen now can't be deleted"
                                      : "Delete license"
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M18.8334 8.5V18.6667C18.8334 19.2275 18.611 19.7654 18.2151 20.1613C17.8192 20.5571 17.2813 20.7797 16.7206 20.7797H7.27941C6.71869 20.7797 6.18078 20.5571 5.78486 20.1613C5.38895 19.7654 5.16642 19.2275 5.16642 18.6667V8.5M3.05536 6.38983H20.9442M14.6109 6.38983V4.27797C14.6109 3.71725 14.3884 3.17934 13.9925 2.78342C13.5966 2.38751 13.0586 2.16498 12.4979 2.16498H11.5016C10.9409 2.16498 10.403 2.38751 10.0071 2.78342C9.61114 3.17934 9.38861 3.71725 9.38861 4.27797V6.38983M10.4447 11.6667V17.6102M13.5553 11.6667V17.6102"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 pt-4 mt-2 w-full">
                        {/* Basic/Premium Section */}
                        <div
                          className={`w-full h-[120px] rounded-xl border ${theme === "dark"
                            ? "bg-[#282828] border-white/5 backdrop-blur-md shadow-inner"
                            : "bg-white border-gray-200 shadow-sm"
                            } flex flex-col items-center justify-center gap-2 transition-all`}
                        >
                          {license.type === LicensesType.BASIC ? (
                            <div className="w-8 h-8 border-none flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <g filter="url(#filter0_n_3530_27520)">
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                                    fill="url(#paint0_linear_3530_27520)"
                                  />
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                                    fill="#C6D92C"
                                    fill-opacity="0.1"
                                  />
                                </g>
                                <circle
                                  cx="12.75"
                                  cy="12.75"
                                  r="10.25"
                                  stroke="url(#paint1_linear_3530_27520)"
                                  stroke-opacity="0.88"
                                />
                                <defs>
                                  <filter
                                    id="filter0_n_3530_27520"
                                    x="2"
                                    y="2"
                                    width="21.5"
                                    height="21.5"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                  >
                                    <feFlood
                                      flood-opacity="0"
                                      result="BackgroundImageFix"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in="SourceGraphic"
                                      in2="BackgroundImageFix"
                                      result="shape"
                                    />
                                    <feTurbulence
                                      type="fractalNoise"
                                      baseFrequency="2 2"
                                      stitchTiles="stitch"
                                      numOctaves="3"
                                      result="noise"
                                      seed="2744"
                                    />
                                    <feColorMatrix
                                      in="noise"
                                      type="luminanceToAlpha"
                                      result="alphaNoise"
                                    />
                                    <feComponentTransfer
                                      in="alphaNoise"
                                      result="coloredNoise1"
                                    >
                                      <feFuncA
                                        type="discrete"
                                        tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                                      />
                                    </feComponentTransfer>
                                    <feComposite
                                      operator="in"
                                      in2="shape"
                                      in="coloredNoise1"
                                      result="noise1Clipped"
                                    />
                                    <feFlood
                                      flood-color="rgba(0, 0, 0, 0.25)"
                                      result="color1Flood"
                                    />
                                    <feComposite
                                      operator="in"
                                      in2="noise1Clipped"
                                      in="color1Flood"
                                      result="color1"
                                    />
                                    <feMerge result="effect1_noise_3530_27520">
                                      <feMergeNode in="shape" />
                                      <feMergeNode in="color1" />
                                    </feMerge>
                                  </filter>
                                  <linearGradient
                                    id="paint0_linear_3530_27520"
                                    x1="12.75"
                                    y1="3"
                                    x2="12.75"
                                    y2="22.5"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#2E2E2E" />
                                    <stop offset="1" stop-color="#666666" />
                                  </linearGradient>
                                  <linearGradient
                                    id="paint1_linear_3530_27520"
                                    x1="2"
                                    y1="12.75"
                                    x2="23.5"
                                    y2="12.75"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#C6D92C" />
                                    <stop
                                      offset="0.533654"
                                      stop-color="#EDF5C0"
                                    />
                                    <stop offset="1" stop-color="#C6D92C" />
                                  </linearGradient>
                                </defs>
                              </svg>{" "}
                            </div>
                          ) : (
                            <div className="w-8 h-8 border-none flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12.75"
                                  cy="12.75"
                                  r="10.75"
                                  fill="url(#paint0_radial_3530_27673)"
                                />
                                <g filter="url(#filter0_n_3530_27673)">
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                                    fill="url(#paint1_linear_3530_27673)"
                                  />
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                                    fill="#C6D92C"
                                    fill-opacity="0.1"
                                  />
                                </g>
                                <circle
                                  cx="12.75"
                                  cy="12.75"
                                  r="10.25"
                                  stroke="url(#paint2_linear_3530_27673)"
                                  stroke-opacity="0.88"
                                />
                                <path
                                  d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                                  stroke="url(#paint3_linear_3530_27673)"
                                  stroke-opacity="0.88"
                                  stroke-width="0.5"
                                />
                                <defs>
                                  <filter
                                    id="filter0_n_3530_27673"
                                    x="2"
                                    y="2"
                                    width="21.5"
                                    height="21.5"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                  >
                                    <feFlood
                                      flood-opacity="0"
                                      result="BackgroundImageFix"
                                    />
                                    <feBlend
                                      mode="normal"
                                      in="SourceGraphic"
                                      in2="BackgroundImageFix"
                                      result="shape"
                                    />
                                    <feTurbulence
                                      type="fractalNoise"
                                      baseFrequency="2 2"
                                      stitchTiles="stitch"
                                      numOctaves="3"
                                      result="noise"
                                      seed="2744"
                                    />
                                    <feColorMatrix
                                      in="noise"
                                      type="luminanceToAlpha"
                                      result="alphaNoise"
                                    />
                                    <feComponentTransfer
                                      in="alphaNoise"
                                      result="coloredNoise1"
                                    >
                                      <feFuncA
                                        type="discrete"
                                        tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                                      />
                                    </feComponentTransfer>
                                    <feComposite
                                      operator="in"
                                      in2="shape"
                                      in="coloredNoise1"
                                      result="noise1Clipped"
                                    />
                                    <feFlood
                                      flood-color="rgba(0, 0, 0, 0.25)"
                                      result="color1Flood"
                                    />
                                    <feComposite
                                      operator="in"
                                      in2="noise1Clipped"
                                      in="color1Flood"
                                      result="color1"
                                    />
                                    <feMerge result="effect1_noise_3530_27673">
                                      <feMergeNode in="shape" />
                                      <feMergeNode in="color1" />
                                    </feMerge>
                                  </filter>
                                  <radialGradient
                                    id="paint0_radial_3530_27673"
                                    cx="0"
                                    cy="0"
                                    r="1"
                                    gradientUnits="userSpaceOnUse"
                                    gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                                  >
                                    <stop stop-color="#D1E156" />
                                    <stop
                                      offset="0.552885"
                                      stop-color="#C6D92C"
                                    />
                                    <stop offset="1" stop-color="#7A8618" />
                                  </radialGradient>
                                  <linearGradient
                                    id="paint1_linear_3530_27673"
                                    x1="12.75"
                                    y1="3"
                                    x2="12.75"
                                    y2="22.5"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#2E2E2E" />
                                    <stop offset="1" stop-color="#666666" />
                                  </linearGradient>
                                  <linearGradient
                                    id="paint2_linear_3530_27673"
                                    x1="2"
                                    y1="12.75"
                                    x2="23.5"
                                    y2="12.75"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#C6D92C" />
                                    <stop
                                      offset="0.533654"
                                      stop-color="#EDF5C0"
                                    />
                                    <stop offset="1" stop-color="#C6D92C" />
                                  </linearGradient>
                                  <linearGradient
                                    id="paint3_linear_3530_27673"
                                    x1="6"
                                    y1="12.7476"
                                    x2="18.5109"
                                    y2="12.7476"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stop-color="#C6D92C" />
                                    <stop
                                      offset="0.533654"
                                      stop-color="#EDF5C0"
                                    />
                                    <stop offset="1" stop-color="#C6D92C" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </div>
                          )}
                          <span
                            className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-gray-900"
                              } uppercase tracking-wide`}
                          >
                            {String(license.type || "--")}
                          </span>
                          <span
                            className={`text-xs ${theme === "dark"
                              ? "text-gray-400"
                              : "text-gray-600"
                              } font-medium`}
                          >
                            {license.screens?.length || 0} Screen
                            {license.screens?.length !== 1 ? "s" : ""}
                          </span>
                        </div>

                        {/* Addon Section - Only show if addOnScreens exist */}
                        {license.addOnScreens &&
                          license.addOnScreens.length > 0 ? (
                          <div
                            className={`relative w-full h-[120px] rounded-xl border ${theme === "dark"
                              ? "bg-[#282828] border-white/5 backdrop-blur-md shadow-inner"
                              : "bg-white border-gray-200 shadow-sm"
                              } flex flex-col items-center justify-center gap-2 ${license.isCancelRequested ||
                                license.isCancelAccepted ||
                                license.status === "cancelled" ||
                                license.status?.toLowerCase() === "expired"
                                ? "opacity-50 cursor-not-allowed"
                                : `cursor-pointer group ${theme === "dark" ? "hover:bg-[#333333]" : "hover:bg-gray-50"}`
                              } transition-all`}
                            onClick={() => {
                              if (
                                !license.isCancelRequested &&
                                !license.isCancelAccepted &&
                                license.status !== "cancelled" &&
                                license.status?.toLowerCase() !== "expired"
                              ) {
                                openAddonDetailsModal(license);
                              }
                            }}
                          >
                            <button
                              className={`absolute top-2 right-2 p-1 transition-colors ${license.isDowngradeRequested ||
                                license.isCancelRequested ||
                                license.isCancelAccepted ||
                                license.status === "cancelled" ||
                                license.status?.toLowerCase() === "expired"
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-500"
                                }`}
                              disabled={
                                !!license.isDowngradeRequested ||
                                !!license.isCancelRequested ||
                                !!license.isCancelAccepted ||
                                license.status === "cancelled" ||
                                license.status?.toLowerCase() === "expired"
                              }
                              title={
                                license.status?.toLowerCase() === "expired"
                                  ? "The license is expired so that addon delete action not able done"
                                  : license.isDowngradeRequested
                                    ? "already downgraded screen now can't be addon delete"
                                    : license.isCancelRequested ||
                                      license.isCancelAccepted ||
                                      license.status === "cancelled"
                                      ? "License cancel is in progress, can't view Addon details"
                                      : ""
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  !license.isCancelRequested &&
                                  !license.isCancelAccepted &&
                                  license.status !== "cancelled" &&
                                  license.status?.toLowerCase() !== "expired"
                                ) {
                                  openAddonDetailsModal(license);
                                }
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
                              </svg>
                            </button>
                            <div className="w-8 h-8 text-[#C6D92C]">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="2"
                                  y="3"
                                  width="20"
                                  height="14"
                                  rx="2"
                                  ry="2"
                                />
                                <line x1="8" y1="21" x2="16" y2="21" />
                                <line x1="12" y1="17" x2="12" y2="21" />
                              </svg>
                            </div>
                            <span
                              className={`text-sm font-bold ${theme === "dark"
                                ? "text-white"
                                : "text-gray-900"
                                } uppercase tracking-wide`}
                            >
                              Addon
                            </span>
                            <span
                              className={`text-xs ${theme === "dark"
                                ? "text-gray-400"
                                : "text-gray-600"
                                } font-medium`}
                            >
                              {license.addOnScreens.length} Screen
                              {license.addOnScreens.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : null}

                        {/* Add Placeholder */}
                        <button
                          type="button"
                          className={`w-full h-[120px] rounded-xl border border-dashed ${theme === "dark"
                            ? "border-[#C6D92C]/50 bg-[#0A0A0A]"
                            : "border-[#C6D92C] bg-[#C6D92C]/10"
                            } flex flex-col items-center justify-center gap-2 ${license.isCancelRequested ||
                              license.isCancelAccepted ||
                              license.status === "cancelled" ||
                              license.status?.toLowerCase() === "expired"
                              ? "opacity-50 cursor-not-allowed"
                              : `cursor-pointer group ${theme === "dark" ? "hover:bg-[#111111]" : "hover:bg-[#C6D92C]/20"}`
                            } transition-transform`}
                          disabled={
                            !!license.isCancelRequested ||
                            !!license.isCancelAccepted ||
                            license.status === "cancelled" ||
                            license.status?.toLowerCase() === "expired"
                          }
                          title={license.status?.toLowerCase() === "expired" ? "The license is expired so that add addon screens action not able done" : ""}
                          onClick={() => {
                            if (
                              !license.isCancelRequested &&
                              !license.isCancelAccepted &&
                              license.status !== "cancelled" &&
                              license.status?.toLowerCase() !== "expired"
                            ) {
                              openAddonScreensModal(license);
                            }
                          }}
                        >
                          <div className="text-[#C6D92C]">
                            <svg
                              width="50"
                              height="50"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="6"
                                y="7"
                                width="12"
                                height="8"
                                rx="1.5"
                                stroke={themeBox ? "white" : "#3A3A3A"}
                                strokeWidth="1"
                              />
                              <line
                                x1="12"
                                y1="15"
                                x2="12"
                                y2="18"
                                stroke={themeBox ? "white" : "#3A3A3A"}
                                strokeWidth="1"
                                strokeLinecap="round"
                              />
                              <line
                                x1="9"
                                y1="18"
                                x2="15"
                                y2="18"
                                stroke={themeBox ? "white" : "#3A3A3A"}
                                strokeWidth="1"
                                strokeLinecap="round"
                              />

                              <line
                                x1="12"
                                y1="9"
                                x2="12"
                                y2="13"
                                stroke={themeBox ? "#C6D92C" : "#3A3A3A"}
                                strokeWidth="1"
                                strokeLinecap="round"
                              />
                              <line
                                x1="10"
                                y1="11"
                                x2="14"
                                y2="11"
                                stroke={themeBox ? "#C6D92C" : "#3A3A3A"}
                                stroke-width="1"
                                stroke-linecap="round"
                              />
                            </svg>
                          </div>
                          <span
                            className={`text-[12px] font-bold ${theme === "dark"
                              ? "text-gray-400"
                              : "text-gray-600"
                              } text-center leading-tight`}
                          >
                            Add Addon  Screens
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {licensesFetching && (
                <div className="py-4 text-center text-sm text-gray-500">
                  Loading more licenses...
                </div>
              )}
            </div>
          </div>



          {!showAddScreenModal &&
            !showCancelPaymentConfirmation &&
            isRazorpay &&
            pendingItem &&
            !isExpired &&
            !isRazorpayOpen && !isPaymentProcessing && (
              <>
                {/* 🔥 BACKDROP */}
                <div
                  className={` z-[9999] fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-300 ease-in-out ${isPendingPurchaseOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
                  onClick={() => setIsPendingPurchaseOpen(false)}
                />

                {/* 🔥 EXPANDED PANEL */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className={`
  fixed bottom-8 right-8 z-[9999] w-[409px]
  max-h-[70vh] overflow-y-auto
  rounded-[14px] border border-[#2a2a2a] p-5 shadow-2xl
  transition-all duration-300 ease-out transform
  ${isPendingPurchaseOpen
                      ? "opacity-100 translate-y-0 scale-100"
                      : "opacity-0 translate-y-10 scale-95 pointer-events-none"}
`}
                  style={{
                    background:
                      "radial-gradient(168.08% 100% at 50% 0%, #2E2E2E 0%, #0F0F0F 100%)",
                  }}
                >
                  {/* Header */}
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[#D92626] font-bold text-[18px]">
                      Purchase Pending
                    </span>

                    {/* 🔥 CLOSE ARROW */}
                    <button onClick={() => setIsPendingPurchaseOpen(false)}>
                      <svg
                        className="rotate-180"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="#F3F3F6"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="border-t border-[#333] mb-4"></div>

                  {/* Info */}
                  <div className="flex justify-between text-white font-bold text-[15px] mb-6">
                    <span>No. License: <span className="text-[#EDF5C0] "> 01 </span></span>
                    <span>
                      Expiry Time:
                      <span className="text-[#EDF5C0] ml-1">
                        <CountdownTimer createdAt={pendingItem.createdAt} />
                      </span>
                    </span>
                  </div>

                  <div className="border-t border-[#333] mb-4"></div>

                  {/* Buttons */}
                  <div className="flex gap-4">
                    {/* Repayment */}
                    <button
                      onClick={handleRepayment}


                    >
                      <svg width="131" height="34" viewBox="0 0 131 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="131" height="34" rx="10" fill="url(#paint0_radial_5345_2094)" fill-opacity="0.4" />
                        <rect x="0.5" y="0.5" width="130" height="33" rx="9.5" stroke="url(#paint1_linear_5345_2094)" stroke-opacity="0.3" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4566 7.75H24.5531C26.393 7.74999 27.838 7.74998 28.9771 7.87859C30.1397 8.00986 31.0825 8.28358 31.8661 8.90218C32.1007 9.08737 32.3183 9.29192 32.5161 9.51366C33.5593 10.6833 33.714 12.2696 33.7499 14.4879C33.7566 14.902 33.4263 15.2432 33.0122 15.2499C32.598 15.2566 32.2568 14.9263 32.2501 14.5121C32.2131 12.2258 32.019 11.2099 31.3967 10.5121C31.2575 10.3561 31.1037 10.2113 30.9367 10.0796C30.4651 9.70729 29.8401 9.48557 28.8088 9.36912C27.7636 9.25112 26.4034 9.25 24.5056 9.25H21.5041C19.6063 9.25 18.246 9.25112 17.2009 9.36912C16.1695 9.48557 15.5445 9.70729 15.0729 10.0796C14.906 10.2113 14.7522 10.3561 14.613 10.5121C14.2262 10.9458 13.9972 11.5149 13.8756 12.4676C13.7514 13.4412 13.75 14.7114 13.75 16.5C13.75 18.2886 13.7514 19.5588 13.8756 20.5324C13.9972 21.4851 14.2262 22.0542 14.613 22.4879C14.7522 22.6439 14.906 22.7887 15.0729 22.9204C15.5445 23.2927 16.1695 23.5144 17.2009 23.6309C18.246 23.7489 19.6063 23.75 21.5041 23.75H22C22.4142 23.75 22.75 24.0858 22.75 24.5C22.75 24.9142 22.4142 25.25 22 25.25H21.4566C19.6167 25.25 18.1717 25.25 17.0326 25.1214C15.87 24.9901 14.9272 24.7164 14.1435 24.0978C13.9089 23.9126 13.6914 23.7081 13.4936 23.4863C12.8271 22.7391 12.5295 21.8334 12.3877 20.7222C12.25 19.6427 12.25 18.2767 12.25 16.5528V16.4472C12.25 14.7232 12.25 13.3573 12.3877 12.2778C12.5295 11.1666 12.8271 10.2609 13.4936 9.51366C13.6914 9.29192 13.9089 9.08737 14.1435 8.90218C14.9272 8.28358 15.87 8.00986 17.0326 7.87859C18.1717 7.74998 19.6166 7.74999 21.4566 7.75Z" fill="white" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M33 14.25H13V12.75H33V14.25Z" fill="white" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M29.7816 16.992C30.0621 16.6873 30.5366 16.6677 30.8413 16.9482L31.6645 17.7061C31.6705 17.7116 31.6764 17.7173 31.6822 17.723C31.6879 17.7286 31.6939 17.7344 31.7001 17.7405C31.7719 17.8108 31.8752 17.9121 31.9498 18.0095C32.0114 18.09 32.2723 18.4435 32.0849 18.8882C31.9003 19.3262 31.4727 19.3916 31.3744 19.4052C31.2532 19.4221 31.1089 19.4219 31.0072 19.4217C30.9985 19.4217 30.9901 19.4217 30.9821 19.4217H27.8777C26.6903 19.4217 25.75 20.3644 25.75 21.5C25.75 21.7584 25.7979 22.0042 25.8851 22.2307C26.0338 22.6173 25.8409 23.0513 25.4543 23.2C25.0677 23.3487 24.6338 23.1559 24.4851 22.7693C24.333 22.374 24.25 21.9457 24.25 21.5C24.25 19.5116 25.8865 17.9217 27.8777 17.9217H29.7129C29.5211 17.64 29.5408 17.2535 29.7816 16.992ZM32.5457 19.8C32.9323 19.6513 33.3662 19.8441 33.515 20.2307C33.667 20.626 33.75 21.0543 33.75 21.5C33.75 23.4884 32.1135 25.0783 30.1223 25.0783H28.2871C28.4789 25.36 28.4592 25.7465 28.2184 26.008C27.9379 26.3127 27.4634 26.3323 27.1587 26.0518L26.3355 25.2939C26.3295 25.2884 26.3236 25.2827 26.3178 25.277C26.3121 25.2714 26.3061 25.2656 26.2999 25.2595C26.2281 25.1892 26.1249 25.0879 26.0503 24.9905C25.9886 24.91 25.7277 24.5565 25.9151 24.1118C26.0997 23.6738 26.5273 23.6084 26.6256 23.5948C26.7468 23.5779 26.8911 23.5781 26.9928 23.5783H27.0179H30.1223C31.3097 23.5783 32.25 22.6356 32.25 21.5C32.25 21.2416 32.2021 20.9958 32.115 20.7693C31.9663 20.3827 32.1591 19.9487 32.5457 19.8Z" fill="#C6D92C" />
                        <path d="M44.0043 22V11.8182H47.8224C48.6046 11.8182 49.2609 11.9541 49.7912 12.2259C50.3248 12.4976 50.7275 12.8788 50.9993 13.3693C51.2744 13.8565 51.4119 14.425 51.4119 15.0746C51.4119 15.7275 51.2727 16.2943 50.9943 16.7749C50.7192 17.2521 50.3132 17.6217 49.7763 17.8835C49.2393 18.142 48.5798 18.2713 47.7976 18.2713H45.0781V16.7401H47.549C48.0064 16.7401 48.3809 16.6771 48.6726 16.5511C48.9643 16.4219 49.1797 16.2346 49.3189 15.9893C49.4614 15.7408 49.5327 15.4358 49.5327 15.0746C49.5327 14.7133 49.4614 14.4051 49.3189 14.1499C49.1764 13.8913 48.9593 13.6958 48.6676 13.5632C48.3759 13.4273 47.9998 13.3594 47.5391 13.3594H45.8487V22H44.0043ZM49.2642 17.3864L51.7848 22H49.7266L47.2507 17.3864H49.2642ZM56.3015 22.1491C55.5359 22.1491 54.8746 21.9901 54.3178 21.6719C53.7643 21.3504 53.3384 20.8963 53.0401 20.3097C52.7418 19.7197 52.5927 19.0253 52.5927 18.2266C52.5927 17.4411 52.7418 16.7517 53.0401 16.1584C53.3417 15.5618 53.7627 15.0978 54.3029 14.7663C54.8432 14.4316 55.4779 14.2642 56.207 14.2642C56.6777 14.2642 57.1218 14.3404 57.5394 14.4929C57.9603 14.642 58.3316 14.8741 58.6531 15.1889C58.9779 15.5038 59.2331 15.9048 59.4187 16.392C59.6043 16.8759 59.6971 17.4527 59.6971 18.1222V18.674H53.4379V17.4609H57.9719C57.9686 17.1162 57.8941 16.8097 57.7482 16.5412C57.6024 16.2694 57.3986 16.0556 57.1367 15.8999C56.8782 15.7441 56.5766 15.6662 56.2319 15.6662C55.864 15.6662 55.5408 15.7557 55.2624 15.9347C54.984 16.1103 54.7669 16.3423 54.6112 16.6307C54.4587 16.9157 54.3808 17.2289 54.3775 17.5703V18.6293C54.3775 19.0734 54.4587 19.4545 54.6211 19.7727C54.7835 20.0876 55.0105 20.3295 55.3022 20.4986C55.5939 20.6643 55.9353 20.7472 56.3263 20.7472C56.5882 20.7472 56.8252 20.7107 57.0373 20.6378C57.2494 20.5616 57.4334 20.4505 57.5891 20.3047C57.7449 20.1589 57.8626 19.9782 57.9421 19.7628L59.6225 19.9517C59.5165 20.3958 59.3143 20.7836 59.016 21.1151C58.721 21.4432 58.3432 21.6984 57.8825 21.8807C57.4218 22.0597 56.8948 22.1491 56.3015 22.1491ZM61.2196 24.8636V14.3636H62.9895V15.6264H63.0939C63.1867 15.4408 63.3176 15.2436 63.4867 15.0348C63.6557 14.8227 63.8844 14.642 64.1728 14.4929C64.4611 14.3404 64.829 14.2642 65.2765 14.2642C65.8664 14.2642 66.3984 14.415 66.8723 14.7166C67.3496 15.0149 67.7275 15.4574 68.0059 16.044C68.2876 16.6274 68.4284 17.3433 68.4284 18.1918C68.4284 19.0303 68.2909 19.7429 68.0158 20.3295C67.7407 20.9162 67.3662 21.3636 66.8922 21.6719C66.4183 21.9801 65.8813 22.1342 65.2814 22.1342C64.8439 22.1342 64.481 22.0613 64.1926 21.9155C63.9043 21.7696 63.6723 21.594 63.4966 21.3885C63.3243 21.1797 63.19 20.9825 63.0939 20.7969H63.0194V24.8636H61.2196ZM62.9846 18.1818C62.9846 18.6757 63.0542 19.1082 63.1934 19.4794C63.3359 19.8506 63.5397 20.1406 63.8049 20.3494C64.0733 20.5549 64.3981 20.6577 64.7793 20.6577C65.177 20.6577 65.5101 20.5516 65.7786 20.3395C66.0471 20.1241 66.2492 19.8307 66.3851 19.4595C66.5243 19.085 66.5939 18.6591 66.5939 18.1818C66.5939 17.7079 66.526 17.2869 66.3901 16.919C66.2542 16.5511 66.052 16.2628 65.7836 16.054C65.5151 15.8452 65.1803 15.7408 64.7793 15.7408C64.3948 15.7408 64.0684 15.8419 63.7999 16.044C63.5314 16.2462 63.3276 16.5296 63.1884 16.8942C63.0525 17.2588 62.9846 17.688 62.9846 18.1818ZM72.1671 22.1541C71.6832 22.1541 71.2473 22.0679 70.8596 21.8956C70.4751 21.7199 70.1702 21.4614 69.9448 21.12C69.7227 20.7786 69.6117 20.3577 69.6117 19.8572C69.6117 19.4264 69.6912 19.0701 69.8503 18.7884C70.0094 18.5066 70.2265 18.2812 70.5016 18.1122C70.7767 17.9432 71.0866 17.8156 71.4313 17.7294C71.7793 17.6399 72.1389 17.5753 72.5101 17.5355C72.9576 17.4891 73.3205 17.4477 73.5989 17.4112C73.8773 17.3714 74.0795 17.3118 74.2054 17.2322C74.3347 17.1494 74.3993 17.0218 74.3993 16.8494V16.8196C74.3993 16.4451 74.2883 16.1551 74.0662 15.9496C73.8442 15.7441 73.5243 15.6413 73.1067 15.6413C72.6659 15.6413 72.3162 15.7375 72.0577 15.9297C71.8025 16.1219 71.6301 16.349 71.5407 16.6108L69.8603 16.3722C69.9928 15.9081 70.2116 15.5204 70.5165 15.2088C70.8214 14.8939 71.1943 14.6586 71.6351 14.5028C72.0759 14.3438 72.5632 14.2642 73.0968 14.2642C73.4647 14.2642 73.8309 14.3073 74.1955 14.3935C74.5601 14.4796 74.8932 14.6222 75.1948 14.821C75.4964 15.0166 75.7383 15.2834 75.9206 15.6214C76.1062 15.9595 76.199 16.3821 76.199 16.8892V22H74.4689V20.951H74.4093C74.2999 21.1631 74.1458 21.362 73.9469 21.5476C73.7514 21.7299 73.5044 21.8774 73.2061 21.9901C72.9112 22.0994 72.5648 22.1541 72.1671 22.1541ZM72.6344 20.8317C72.9957 20.8317 73.3089 20.7604 73.574 20.6179C73.8392 20.4721 74.043 20.2798 74.1855 20.0412C74.3314 19.8026 74.4043 19.5424 74.4043 19.2607V18.3608C74.348 18.4072 74.2518 18.4503 74.1159 18.4901C73.9834 18.5298 73.8342 18.5646 73.6685 18.5945C73.5028 18.6243 73.3387 18.6508 73.1763 18.674C73.0139 18.6972 72.873 18.7171 72.7537 18.7337C72.4853 18.7701 72.245 18.8298 72.0328 18.9126C71.8207 18.9955 71.6534 19.1115 71.5307 19.2607C71.4081 19.4065 71.3468 19.5954 71.3468 19.8274C71.3468 20.1589 71.4677 20.4091 71.7097 20.5781C71.9516 20.7472 72.2599 20.8317 72.6344 20.8317ZM78.8539 24.8636C78.6086 24.8636 78.3816 24.8438 78.1728 24.804C77.9673 24.7675 77.8032 24.7244 77.6806 24.6747L78.0982 23.2727C78.36 23.349 78.5937 23.3854 78.7992 23.3821C79.0047 23.3788 79.1853 23.3142 79.3411 23.1882C79.5002 23.0656 79.6344 22.8601 79.7438 22.5717L79.8979 22.1591L77.1287 14.3636H79.0378L80.7978 20.1307H80.8773L82.6422 14.3636H84.5563L81.4988 22.9247C81.3562 23.3291 81.1673 23.6754 80.932 23.9638C80.6967 24.2554 80.4083 24.4775 80.0669 24.63C79.7289 24.7857 79.3245 24.8636 78.8539 24.8636ZM85.8017 22V14.3636H87.5218V15.6612H87.6113C87.7704 15.2237 88.0339 14.8823 88.4018 14.6371C88.7697 14.3885 89.2089 14.2642 89.7193 14.2642C90.2363 14.2642 90.6722 14.3902 91.0268 14.642C91.3848 14.8906 91.6367 15.2304 91.7825 15.6612H91.862C92.0311 15.237 92.3161 14.8989 92.7172 14.647C93.1215 14.3918 93.6004 14.2642 94.1539 14.2642C94.8566 14.2642 95.43 14.4863 95.8741 14.9304C96.3182 15.3745 96.5403 16.0225 96.5403 16.8743V22H94.7356V17.1527C94.7356 16.6787 94.6097 16.3324 94.3578 16.1136C94.1059 15.8916 93.7976 15.7805 93.4331 15.7805C92.9989 15.7805 92.6592 15.9164 92.4139 16.1882C92.1719 16.4567 92.051 16.8063 92.051 17.2372V22H90.286V17.0781C90.286 16.6837 90.1667 16.3688 89.9281 16.1335C89.6928 15.8982 89.3845 15.7805 89.0034 15.7805C88.7449 15.7805 88.5095 15.8468 88.2974 15.9794C88.0853 16.1087 87.9163 16.2926 87.7903 16.5312C87.6644 16.7666 87.6014 17.0417 87.6014 17.3565V22H85.8017ZM101.747 22.1491C100.981 22.1491 100.32 21.9901 99.7631 21.6719C99.2096 21.3504 98.7837 20.8963 98.4854 20.3097C98.1871 19.7197 98.038 19.0253 98.038 18.2266C98.038 17.4411 98.1871 16.7517 98.4854 16.1584C98.7871 15.5618 99.208 15.0978 99.7482 14.7663C100.288 14.4316 100.923 14.2642 101.652 14.2642C102.123 14.2642 102.567 14.3404 102.985 14.4929C103.406 14.642 103.777 14.8741 104.098 15.1889C104.423 15.5038 104.678 15.9048 104.864 16.392C105.05 16.8759 105.142 17.4527 105.142 18.1222V18.674H98.8832V17.4609H103.417C103.414 17.1162 103.339 16.8097 103.194 16.5412C103.048 16.2694 102.844 16.0556 102.582 15.8999C102.324 15.7441 102.022 15.6662 101.677 15.6662C101.309 15.6662 100.986 15.7557 100.708 15.9347C100.429 16.1103 100.212 16.3423 100.056 16.6307C99.904 16.9157 99.8261 17.2289 99.8228 17.5703V18.6293C99.8228 19.0734 99.904 19.4545 100.066 19.7727C100.229 20.0876 100.456 20.3295 100.748 20.4986C101.039 20.6643 101.381 20.7472 101.772 20.7472C102.033 20.7472 102.27 20.7107 102.483 20.6378C102.695 20.5616 102.879 20.4505 103.034 20.3047C103.19 20.1589 103.308 19.9782 103.387 19.7628L105.068 19.9517C104.962 20.3958 104.76 20.7836 104.461 21.1151C104.166 21.4432 103.788 21.6984 103.328 21.8807C102.867 22.0597 102.34 22.1491 101.747 22.1491ZM108.465 17.5256V22H106.665V14.3636H108.385V15.6612H108.475C108.65 15.2337 108.93 14.8939 109.315 14.642C109.703 14.3902 110.182 14.2642 110.752 14.2642C111.279 14.2642 111.738 14.3769 112.129 14.6023C112.523 14.8277 112.828 15.1541 113.044 15.5817C113.262 16.0092 113.37 16.5279 113.367 17.1378V22H111.567V17.4162C111.567 16.9058 111.434 16.5064 111.169 16.218C110.907 15.9297 110.544 15.7855 110.08 15.7855C109.766 15.7855 109.485 15.8551 109.24 15.9943C108.998 16.1302 108.808 16.3274 108.669 16.5859C108.533 16.8445 108.465 17.1577 108.465 17.5256ZM119.009 14.3636V15.7557H114.619V14.3636H119.009ZM115.703 12.5341H117.503V19.7031C117.503 19.9451 117.539 20.1307 117.612 20.2599C117.689 20.3859 117.788 20.4721 117.911 20.5185C118.033 20.5649 118.169 20.5881 118.318 20.5881C118.431 20.5881 118.534 20.5798 118.627 20.5632C118.723 20.5466 118.796 20.5317 118.845 20.5185L119.149 21.9254C119.052 21.9586 118.915 21.995 118.736 22.0348C118.56 22.0746 118.345 22.0978 118.09 22.1044C117.639 22.1177 117.233 22.0497 116.872 21.9006C116.51 21.7481 116.224 21.5128 116.012 21.1946C115.803 20.8764 115.7 20.4787 115.703 20.0014V12.5341Z" fill="white" />
                        <defs>
                          <radialGradient id="paint0_radial_5345_2094" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(68.7426) rotate(90) scale(34 123.364)">
                            <stop stop-color="#C6D92C" />
                            <stop offset="1" stop-color="#535B10" />
                          </radialGradient>
                          <linearGradient id="paint1_linear_5345_2094" x1="0" y1="17" x2="131" y2="17" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#C6D92C" />
                            <stop offset="0.5" stop-color="#EDF5C0" />
                            <stop offset="1" stop-color="#C6D92C" />
                          </linearGradient>
                        </defs>
                      </svg>

                    </button>

                    {/* Cancel */}
                    <button onClick={() => {
                      setShowCancelPaymentConfirmation(true)
                    }} >

                      <svg width="80" height="34" viewBox="0 0 80 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="80" height="34" rx="10" fill="black" fill-opacity="0.01" />
                        <rect x="0.5" y="0.5" width="79" height="33" rx="9.5" stroke="#FAFCED" stroke-opacity="0.3" />
                        <path d="M25.7195 15.2536H23.8601C23.8071 14.9486 23.7093 14.6785 23.5668 14.4432C23.4242 14.2045 23.2469 14.0024 23.0348 13.8366C22.8227 13.6709 22.5807 13.5466 22.3089 13.4638C22.0405 13.3776 21.7505 13.3345 21.4389 13.3345C20.8854 13.3345 20.3949 13.4737 19.9673 13.7521C19.5398 14.0272 19.205 14.4316 18.9631 14.9652C18.7211 15.4955 18.6001 16.1435 18.6001 16.9091C18.6001 17.688 18.7211 18.3442 18.9631 18.8778C19.2083 19.4081 19.5431 19.8092 19.9673 20.081C20.3949 20.3494 20.8838 20.4837 21.4339 20.4837C21.7389 20.4837 22.0239 20.4439 22.2891 20.3643C22.5575 20.2815 22.7978 20.1605 23.0099 20.0014C23.2254 19.8423 23.406 19.6468 23.5518 19.4148C23.701 19.1828 23.8037 18.9176 23.8601 18.6193L25.7195 18.6293C25.6499 19.1132 25.4991 19.5672 25.267 19.9915C25.0384 20.4157 24.7384 20.7902 24.3672 21.1151C23.996 21.4366 23.5618 21.6884 23.0646 21.8707C22.5675 22.0497 22.0156 22.1392 21.4091 22.1392C20.5142 22.1392 19.7154 21.9321 19.0128 21.5178C18.3101 21.1035 17.7566 20.5052 17.3523 19.723C16.9479 18.9408 16.7457 18.0028 16.7457 16.9091C16.7457 15.812 16.9496 14.8741 17.3572 14.0952C17.7649 13.313 18.3201 12.7147 19.0227 12.3004C19.7254 11.8861 20.5208 11.679 21.4091 11.679C21.9759 11.679 22.5028 11.7585 22.9901 11.9176C23.4773 12.0767 23.9115 12.3104 24.2926 12.6186C24.6738 12.9235 24.987 13.2981 25.2322 13.7422C25.4808 14.183 25.6432 14.6868 25.7195 15.2536ZM29.5265 22.1541C29.0426 22.1541 28.6067 22.0679 28.2189 21.8956C27.8345 21.7199 27.5295 21.4614 27.3042 21.12C27.0821 20.7786 26.9711 20.3577 26.9711 19.8572C26.9711 19.4264 27.0506 19.0701 27.2097 18.7884C27.3688 18.5066 27.5859 18.2812 27.861 18.1122C28.1361 17.9432 28.446 17.8156 28.7907 17.7294C29.1387 17.6399 29.4983 17.5753 29.8695 17.5355C30.3169 17.4891 30.6799 17.4477 30.9583 17.4112C31.2367 17.3714 31.4389 17.3118 31.5648 17.2322C31.6941 17.1494 31.7587 17.0218 31.7587 16.8494V16.8196C31.7587 16.4451 31.6477 16.1551 31.4256 15.9496C31.2035 15.7441 30.8837 15.6413 30.4661 15.6413C30.0253 15.6413 29.6756 15.7375 29.4171 15.9297C29.1619 16.1219 28.9895 16.349 28.9 16.6108L27.2196 16.3722C27.3522 15.9081 27.571 15.5204 27.8759 15.2088C28.1808 14.8939 28.5537 14.6586 28.9945 14.5028C29.4353 14.3438 29.9225 14.2642 30.4561 14.2642C30.824 14.2642 31.1903 14.3073 31.5549 14.3935C31.9194 14.4796 32.2525 14.6222 32.5542 14.821C32.8558 15.0166 33.0977 15.2834 33.28 15.6214C33.4656 15.9595 33.5584 16.3821 33.5584 16.8892V22H31.8283V20.951H31.7686C31.6593 21.1631 31.5051 21.362 31.3063 21.5476C31.1107 21.7299 30.8638 21.8774 30.5655 21.9901C30.2705 22.0994 29.9242 22.1541 29.5265 22.1541ZM29.9938 20.8317C30.3551 20.8317 30.6683 20.7604 30.9334 20.6179C31.1986 20.4721 31.4024 20.2798 31.5449 20.0412C31.6908 19.8026 31.7637 19.5424 31.7637 19.2607V18.3608C31.7073 18.4072 31.6112 18.4503 31.4753 18.4901C31.3427 18.5298 31.1936 18.5646 31.0279 18.5945C30.8622 18.6243 30.6981 18.6508 30.5357 18.674C30.3733 18.6972 30.2324 18.7171 30.1131 18.7337C29.8446 18.7701 29.6043 18.8298 29.3922 18.9126C29.1801 18.9955 29.0127 19.1115 28.8901 19.2607C28.7675 19.4065 28.7061 19.5954 28.7061 19.8274C28.7061 20.1589 28.8271 20.4091 29.0691 20.5781C29.311 20.7472 29.6193 20.8317 29.9938 20.8317ZM37.1678 17.5256V22H35.3681V14.3636H37.0882V15.6612H37.1777C37.3534 15.2337 37.6335 14.8939 38.0179 14.642C38.4057 14.3902 38.8846 14.2642 39.4547 14.2642C39.9817 14.2642 40.4408 14.3769 40.8319 14.6023C41.2263 14.8277 41.5312 15.1541 41.7466 15.5817C41.9654 16.0092 42.0731 16.5279 42.0698 17.1378V22H40.2701V17.4162C40.2701 16.9058 40.1375 16.5064 39.8723 16.218C39.6105 15.9297 39.2476 15.7855 38.7836 15.7855C38.4687 15.7855 38.1886 15.8551 37.9434 15.9943C37.7014 16.1302 37.5108 16.3274 37.3716 16.5859C37.2357 16.8445 37.1678 17.1577 37.1678 17.5256ZM47.2303 22.1491C46.468 22.1491 45.8134 21.9818 45.2665 21.647C44.723 21.3123 44.3037 20.8499 44.0087 20.2599C43.717 19.6667 43.5712 18.9839 43.5712 18.2116C43.5712 17.4361 43.7203 16.7517 44.0186 16.1584C44.3169 15.5618 44.7379 15.0978 45.2814 14.7663C45.8283 14.4316 46.4746 14.2642 47.2203 14.2642C47.8401 14.2642 48.3887 14.3786 48.8659 14.6072C49.3465 14.8326 49.7293 15.1525 50.0144 15.5668C50.2994 15.9777 50.4618 16.4583 50.5016 17.0085H48.7814C48.7118 16.6406 48.5461 16.334 48.2843 16.0888C48.0257 15.8402 47.6794 15.7159 47.2452 15.7159C46.8773 15.7159 46.5542 15.8153 46.2757 16.0142C45.9973 16.2098 45.7802 16.4915 45.6245 16.8594C45.472 17.2273 45.3958 17.6681 45.3958 18.1818C45.3958 18.7022 45.472 19.1496 45.6245 19.5241C45.7769 19.8954 45.9907 20.1821 46.2658 20.3842C46.5442 20.5831 46.8707 20.6825 47.2452 20.6825C47.5104 20.6825 47.7473 20.6328 47.9561 20.5334C48.1683 20.4306 48.3456 20.2831 48.4881 20.0909C48.6306 19.8987 48.7284 19.665 48.7814 19.3899H50.5016C50.4585 19.9302 50.2994 20.4091 50.0243 20.8267C49.7492 21.241 49.3747 21.5658 48.9007 21.8011C48.4268 22.0331 47.87 22.1491 47.2303 22.1491ZM55.3601 22.1491C54.5945 22.1491 53.9332 21.9901 53.3764 21.6719C52.8229 21.3504 52.397 20.8963 52.0987 20.3097C51.8004 19.7197 51.6513 19.0253 51.6513 18.2266C51.6513 17.4411 51.8004 16.7517 52.0987 16.1584C52.4003 15.5618 52.8213 15.0978 53.3615 14.7663C53.9018 14.4316 54.5365 14.2642 55.2656 14.2642C55.7363 14.2642 56.1804 14.3404 56.598 14.4929C57.0189 14.642 57.3902 14.8741 57.7116 15.1889C58.0365 15.5038 58.2917 15.9048 58.4773 16.392C58.6629 16.8759 58.7557 17.4527 58.7557 18.1222V18.674H52.4964V17.4609H57.0305C57.0272 17.1162 56.9527 16.8097 56.8068 16.5412C56.661 16.2694 56.4571 16.0556 56.1953 15.8999C55.9368 15.7441 55.6352 15.6662 55.2905 15.6662C54.9226 15.6662 54.5994 15.7557 54.321 15.9347C54.0426 16.1103 53.8255 16.3423 53.6697 16.6307C53.5173 16.9157 53.4394 17.2289 53.4361 17.5703V18.6293C53.4361 19.0734 53.5173 19.4545 53.6797 19.7727C53.8421 20.0876 54.0691 20.3295 54.3608 20.4986C54.6525 20.6643 54.9938 20.7472 55.3849 20.7472C55.6468 20.7472 55.8838 20.7107 56.0959 20.6378C56.308 20.5616 56.492 20.4505 56.6477 20.3047C56.8035 20.1589 56.9212 19.9782 57.0007 19.7628L58.6811 19.9517C58.575 20.3958 58.3729 20.7836 58.0746 21.1151C57.7796 21.4432 57.4018 21.6984 56.9411 21.8807C56.4804 22.0597 55.9534 22.1491 55.3601 22.1491ZM62.0779 11.8182V22H60.2782V11.8182H62.0779Z" fill="white" />
                      </svg>


                    </button>
                  </div>
                </div>

                {/* 🔥 BOTTOM BAR */}

                <button
                  onClick={() => setIsPendingPurchaseOpen((prev) => !prev)}
                  className={`fixed bottom-8 right-8 z-[9999] w-[400px] h-[60px] rounded-[98px] flex items-center justify-between px-6 shadow-2xl transition-all duration-300 ease-out transform overflow-hidden ${!isPendingPurchaseOpen
                    ? "opacity-100 translate-y-0 scale-100 pointer-events-auto hover:scale-[1.02]"
                    : "opacity-0 translate-y-8 scale-95 pointer-events-none"
                    }`}
                  style={{
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.8) 0%, rgba(91, 16, 16, 0.8) 100%)",
                    backdropFilter: "blur(8px)", // Optional: Adds a nice glass effect since your background has 0.8 opacity
                  }}
                >
                  {/* Left Side: Main Label */}
                  <span className="font-semibold text-sm text-[#F3F3F6] tracking-wide">
                    Purchase Pending
                  </span>

                  {/* Right Side: Timer & Arrow */}
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-sm text-[#F3F3F6] flex items-center gap-1.5">
                      Expiry Time:
                      {/* Emphasize the timer itself */}
                      <span className="font-bold text-[#EDF5C0] tracking-wider">
                        <CountdownTimer createdAt={pendingItem.createdAt} />
                      </span>
                    </span>

                    {/* 🔥 ARROW ROTATION */}
                    <svg
                      className={`transition-transform duration-300 ${isPendingPurchaseOpen ? "rotate-180" : "rotate-0"
                        }`}
                      width="20" // Slightly smaller arrow to match the text hierarchy
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="#F3F3F6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>





              </>
            )}


        </div>
      </div>

      <AddScreenModal
        isOpen={showAddScreenModal}
        onClose={closeAddScreenModal}
        onSuccess={handleSuccess}          // ✅ FIXED
        onPaymentFailed={handlePaymentFailed}

      />

      <AddonScreensModal
        isOpen={showAddonScreensModal}
        onClose={closeAddonScreensModal}
        onSuccess={handleScreenAdded}
        license={selectedLicenseForAddon}
        onPaymentFailed={handlePaymentFailed}
      />
      <AddonScreenDetailsModal
        isOpen={showAddonDetailsModal}
        onClose={closeAddonDetailsModal}
        license={selectedLicense}
        addOnScreens={addonDetailsScreens}
        onDeleteScreen={(screenId: string) => {
          if (selectedLicense) {
            handleOpenAddonDelete(selectedLicense._id, screenId);
          }
        }}
      />
      <LicenseDetailsModal
        isOpen={showLicenseDetailsModal}
        onClose={closeLicenseDetailsModal}
        license={selectedLicense}
        licenseScreens={licenseDetailsScreens.licenseScreens}
        addOnScreens={licenseDetailsScreens.addOnScreens}
        onDeleteScreen={(screenId: string) => {
          if (selectedLicense) {
            handleOpenAddonDelete(selectedLicense._id, screenId);
          }
        }}
      />

      <DeleteLicenseModal
        isOpen={showDeleteLicenseModal}
        onClose={closeDeleteLicenseModal}
        onDelete={async () => {
          if (!selectedLicense?._id) {
            console.error("No license ID found for deletion");
            return;
          }
          try {
            await cancelLicense({ licenseId: selectedLicense._id }).unwrap();
            console.log("License deleted successfully!");
          } catch (e) {
            console.error("Failed to delete license:", e);
          }
          closeDeleteLicenseModal();
        }}
        license={selectedLicense}
        screens={deleteModalScreens}
      />

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="Delete Screen"
        message={`Are you sure you want to delete screen ?`}
        confirmText="Confirm and delete"
        type="delete"
      />

      <ConfirmActionModal
        isOpen={showAddonDeleteConfirm}
        onClose={closeAddonDelete}
        onConfirm={handleConfirmAddonDelete}
        title="Delete Addon Screen"
        message="Are you sure you want to delete this addon screen?"
        confirmText="Confirm and delete"
        type="delete"
        licenseId={addonDeleteInfo?.licenseId}
        addonScreenId={addonDeleteInfo?.screenId}
      />

      <ConfirmActionModal
        isOpen={showRevokeConfirm}
        onClose={closeRevokeConfirm}
        onConfirm={confirmRevoke}
        title="Revoke License Cancellation"
        message="Are you sure you want to revoke the cancellation request for this license?"
        confirmText="Confirm Revoke"
        type="delete" // using delete style for critical action, or plain if preferred
      />

      <ConfirmActionModal
        isOpen={showResubscribeConfirm}
        onClose={closeResubscribeConfirm}
        onConfirm={confirmResubscribe}
        title="Resubscribe License"
        message="Are you sure you want to resubscribe to this license?"
        confirmText="Confirm Resubscribe"
        type="action"
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={closeUpgradeModal}
        onConfirm={confirmUpgrade}
        selectedScreen={selectedScreen}
      />

      <ComparePlansModal
        isOpen={showComparePlansModal}
        onClose={closeComparePlansModal}
      />
      <DowngradeModal
        isOpen={showDowngradeModal}
        onClose={closeDowngradeModal}
        onConfirm={confirmDowngrade}
        billingDate={formattedBillDate}
      />

      <CancelPaymentConfirmation
        isOpen={showCancelPaymentConfirmation}
        title="Cancel Pending Purchase"
        message="Do you really want to cancel this purchase?"
        onClose={() => { setShowCancelPaymentConfirmation(false) }}
        onConfirm={handleCancelPendingSubscription}
      // isLoading={isCancelingPendingSubscription}
      />
    </div >
  );
};

export default Licenses;
